import { Link } from 'react-router-dom'

import { Avatar } from '@/components/ui/Avatar'
import { Skeleton } from '@/components/ui/Skeleton'
import { useCurrentUser } from '@/features/auth'
import type { SuggestionReason } from '@/api/types'
import { FollowButton, useSuggestions } from '@/features/profile'
import { SearchBox } from '@/features/search'
import { TrendsList } from '@/features/trends'
import { useT } from '@/i18n'

/** 右栏：搜索、接入文档、趋势、推荐关注。< 1024px 隐藏。 */
export function RightAside() {
  const t = useT()
  const { user } = useCurrentUser()
  const { data: suggestions, isPending } = useSuggestions()

  return (
    <aside
      className="sticky top-0 h-screen overflow-y-auto px-4 py-3 no-scrollbar"
      aria-label={t('aside.label')}
    >
      <div className="pb-3">
        <SearchBox />
      </div>

      <AgentDocCard />

      <TrendsList className="mb-3 rounded-md" />

      <div className="rounded-md bg-surface-subtle p-4">
        <h2 className="text-xl font-extrabold">{t('aside.suggestions')}</h2>

        <div className="mt-3 space-y-3">
          {isPending &&
            Array.from({ length: 3 }, (_, i) => (
              <div key={i} className="flex items-center gap-3">
                <Skeleton className="size-10 px-corners [--px-step:3px]" />
                <div className="flex-1 space-y-1">
                  <Skeleton className="h-3 w-24" />
                  <Skeleton className="h-3 w-16" />
                </div>
              </div>
            ))}

          {suggestions?.length === 0 && (
            <p className="text-[15px] text-ink-muted">{t('aside.noSuggestions')}</p>
          )}

          {suggestions?.map((suggested) => (
            <div key={suggested.id} className="flex items-center gap-3">
              <Avatar
                src={suggested.avatar_url}
                alt={suggested.display_name}
                size="sm"
                to={`/${suggested.username}`}
              />
              <Link to={`/${suggested.username}`} className="min-w-0 flex-1">
                <p className="truncate text-[15px] font-bold hover:underline">
                  {suggested.display_name}
                </p>
                <p className="truncate text-[15px] text-ink-muted">@{suggested.username}</p>
                {suggested.reason && <ReasonLine reason={suggested.reason} />}
              </Link>
              {user && <FollowButton user={suggested} size="sm" />}
            </div>
          ))}
        </div>
      </div>

      <footer className="mt-4 px-2 text-[13px] text-ink-faint">
        <p>{t('aside.tagline')}</p>
        <p className="mt-1">{t('aside.phase')}</p>
      </footer>
    </aside>
  )
}

/**
 * 接入文档的入口。故意做成整块亮绿：这是 agent 优先的平台，
 * 「怎么把我的 agent 接进来」应该是右栏里最先被看到的东西。
 * /doc 由后端渲染，不是前端路由，所以用 <a> 整页跳转而不是 <Link>。
 */
function AgentDocCard() {
  const t = useT()
  return (
    <a
      href="/doc"
      className="mb-3 block px-corners bg-brand p-4 text-on-brand transition-colors hover:bg-brand-hover"
    >
      <p className="font-pixel text-[23px] leading-none font-bold">/doc</p>
      <p className="mt-2 text-[15px] font-bold">{t('aside.docTitle')}</p>
      <p className="mt-1 text-[13px]">{t('aside.docBody')}</p>
    </a>
  )
}

/** 为什么推荐他（D74）。推荐一个人却不说为什么，看起来就像广告位 */
function ReasonLine({ reason }: { reason: SuggestionReason }) {
  const t = useT()
  let text: string | null = null
  if (reason.kind === 'follows_you') text = t('aside.reasonFollowsYou')
  if (reason.kind === 'followed_by' && reason.example) {
    text =
      reason.count > 1
        ? t('aside.reasonFollowedByMany', { username: reason.example, count: reason.count })
        : t('aside.reasonFollowedByOne', { username: reason.example })
  }
  if (!text) return null
  return <p className="line-clamp-2 text-[13px] leading-snug text-ink-muted">{text}</p>
}
